import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styles from './paymentFailed.module.css';
import paymentFailedIcon from '../../assets/images/verticaltv/payment-failed.png';
import { CLEVERTAP_EVENTS } from '../../analytics/trackerEvents/cleverTap/events';
import { CT_PARAMS } from '../../analytics/trackerEvents/cleverTap/parameters';
import { trackCleverTapEvent } from '../../utils/cleverTapHelper';
import { useTokenStore } from '../../storage/tokenState';
import { NAVIGATION_ROUTES } from '../../constant';
import { useGetOrderStatus } from '../../api/getOrderStatus';
import { useSubscriptionPaymentStore } from '../../storage/subscriptionPaymentStore';

const PaymentFailed: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useTokenStore();
  const { getOrderStatus } = useGetOrderStatus();
  const resetSubscriptionPaymentDataCtx = useSubscriptionPaymentStore((state)=>state.resetSubscriptionPaymentDataCtx);

  useEffect(()=>{
    resetSubscriptionPaymentDataCtx();
  },[]);

  // Check order status and push CT event when component mounts
  useEffect(() => {
    const checkOrderStatusAndPushEvent = async () => {
      const stateOrderId = location.state?.orderId;
      const storedOrderId = sessionStorage.getItem('pendingOrderId');
      const finalOrderId = stateOrderId || storedOrderId;
      
      if (finalOrderId) {
        try {
          const response = await getOrderStatus({ orderId: finalOrderId });
          const orderStatus = response?.orderStatus || response?.response?.data?.status || 'FAILED';
          
          // Push CleverTap event with orderStatus
          const userId = token?.zupeeUserId?.toString() || '';
          trackCleverTapEvent(CLEVERTAP_EVENTS.WEB_EVENT_STATUS_SCREEN_VIEW, {
            [CT_PARAMS.USER_ID]: userId,
            [CT_PARAMS.ORDER_ID]: finalOrderId,
            orderStatus: orderStatus
          });
        } catch (error) {
          console.error('[PaymentFailed] Error checking order status:', error);
          // Push event with FAILED status as fallback
          const userId = token?.zupeeUserId?.toString() || '';
          const finalOrderId = location.state?.orderId || sessionStorage.getItem('pendingOrderId');
          if (finalOrderId) {
            trackCleverTapEvent(CLEVERTAP_EVENTS.WEB_EVENT_STATUS_SCREEN_VIEW, {
              [CT_PARAMS.USER_ID]: userId,
              [CT_PARAMS.ORDER_ID]: finalOrderId,
              orderStatus: 'FAILED'
            });
          }
        }
      }
    };
    
    checkOrderStatusAndPushEvent();
  }, [location, getOrderStatus, token]);

  const handleRetry = () => {
    // Track CleverTap event for retry payment click
    const userId = token?._id || token?.zupeeUserId?.toString() || '';
    trackCleverTapEvent(CLEVERTAP_EVENTS.WEB_EVENT_RETRY_PAYMENT_CLICKED, {
      [CT_PARAMS.USER_ID]: userId
    });
    
    // Navigate to subscription payment screen
    navigate(NAVIGATION_ROUTES.REFERRAL_SUBSCRIPTION_PAYMENT);
  };

  return (
    <div className={styles.container}>
      {/* Background Gradient SVG */}
      <div className={styles.backgroundGradient}>
        {/* Bottom Left Corner SVG */}
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          width="360" 
          height="621" 
          viewBox="0 0 360 621" 
          fill="none"
          className={styles.bottomLeftSvg}
        >
          <g filter="url(#filter0_fn_531_2611)">
            <circle cx="107" cy="592" r="158" fill="#8B2A1F"/>
          </g>
          <defs>
            <filter id="filter0_fn_531_2611" x="-485" y="0" width="1184" height="1184" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
              <feFlood floodOpacity="0" result="BackgroundImageFix"/>
              <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
              <feGaussianBlur stdDeviation="217" result="effect1_foregroundBlur_531_2611"/>
              <feTurbulence type="fractalNoise" baseFrequency="0.5 0.5" stitchTiles="stitch" numOctaves="3" result="noise" seed="1097"/>
              <feComponentTransfer in="noise" result="coloredNoise1">
                <feFuncR type="linear" slope="2" intercept="-0.5"/>
                <feFuncG type="linear" slope="2" intercept="-0.5"/>
                <feFuncB type="linear" slope="2" intercept="-0.5"/>
                <feFuncA type="discrete" tableValues="1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 "/>
              </feComponentTransfer>
              <feComposite operator="in" in2="effect1_foregroundBlur_531_2611" in="coloredNoise1" result="noise1Clipped"/>
              <feComponentTransfer in="noise1Clipped" result="color1">
                <feFuncA type="table" tableValues="0 0.3"/>
              </feComponentTransfer>
              <feMerge result="effect2_noise_531_2611">
                <feMergeNode in="effect1_foregroundBlur_531_2611"/>
                <feMergeNode in="color1"/>
              </feMerge>
            </filter>
          </defs>
        </svg>
        {/* Top Right Corner SVG */}
        <svg xmlns="http://www.w3.org/2000/svg" width="360" height="684" viewBox="0 0 360 684" fill="none" className={styles.backgroundSvg}>
          <g filter="url(#filter0_fn_558_8442)">
            <circle cx="360" cy="92" r="158" fill="#B6352C"/>
          </g>
          <defs>
            <filter id="filter0_fn_558_8442" x="-232" y="-500" width="1184" height="1184" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
              <feFlood floodOpacity="0" result="BackgroundImageFix"/>
              <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
              <feGaussianBlur stdDeviation="217" result="effect1_foregroundBlur_558_8442"/>
              <feTurbulence type="fractalNoise" baseFrequency="0.5 0.5" stitchTiles="stitch" numOctaves="3" result="noise" seed="2135"/>
              <feComponentTransfer in="noise" result="coloredNoise1">
                <feFuncR type="linear" slope="2" intercept="-0.5"/>
                <feFuncG type="linear" slope="2" intercept="-0.5"/>
                <feFuncB type="linear" slope="2" intercept="-0.5"/>
                <feFuncA type="discrete" tableValues="1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 "/>
              </feComponentTransfer>
              <feComposite operator="in" in2="effect1_foregroundBlur_558_8442" in="coloredNoise1" result="noise1Clipped"/>
              <feComponentTransfer in="noise1Clipped" result="color1">
                <feFuncA type="table" tableValues="0 0.4"/>
              </feComponentTransfer>
              <feMerge result="effect2_noise_558_8442">
                <feMergeNode in="effect1_foregroundBlur_558_8442"/>
                <feMergeNode in="color1"/>
              </feMerge>
            </filter>
          </defs>
        </svg>
      </div>

      {/* Main Content */}
      <div className={styles.content}>
        {/* Error Icon */}
        <div className={styles.iconContainer}>
          <img src={paymentFailedIcon} alt="Payment Failed" className={styles.icon} />
        </div>

        {/* Title */}
        <h2 className={styles.title}>Payment failed</h2>

        {/* Subtitle */}
        <p className={styles.subtitle}>
          Amount will be refunded back to your account if deducted
        </p>
      </div>

      {/* Retry Button */}
      <button className={styles.retryButton} onClick={handleRetry}>
        Retry
      </button>
    </div>
  );
};

export default PaymentFailed;

